import {zodResolver} from "@hookform/resolvers/zod";
import type {ICompany, JobCategory} from "@mytour/shared";
import {JOBS_BY_CATEGORY, JobCategoryArray} from "@mytour/shared";
import {Splitter} from "@mytour/ui/layout";
import {Controller, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Button, Form, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateBusinessDescriptor} from "../../query/companySettingsQueries";

type Props = {
  company: ICompany;
};

const JOB_CATEGORIES = [
  {value: "", label: "--"},
  {value: "Soins animaliers", label: "Soins animaliers"},
  {value: "Soin humain", label: "Soin humain"},
  {value: "Entretien", label: "Entretien"},
  {value: "Dépannage", label: "Dépannage"},
  {value: "Autre", label: "Autre"},
];

const businessDescriptorSchema = z.object({
  jobCategory: z.string(),
  jobTitle: z.string(),
});

type BusinessDescriptorFormData = z.infer<typeof businessDescriptorSchema>;

export function BusinessDescriptorTab({company}: Props) {
  const {updateBusinessDescriptor, isUpdatingBusinessDescriptor} = useUpdateBusinessDescriptor();
  const disabled = isUpdatingBusinessDescriptor;

  const {control, handleSubmit, watch} = useForm<BusinessDescriptorFormData>({
    resolver: zodResolver(businessDescriptorSchema),
    defaultValues: {
      jobCategory: company.features.jobCategory || "",
      jobTitle: company.features.jobTitle || "",
    },
  });

  const jobCategory = watch("jobCategory");
  const availableJobs =
    jobCategory && JobCategoryArray.includes(jobCategory as JobCategory)
      ? JOBS_BY_CATEGORY[jobCategory as JobCategory]
      : [];

  return (
    <div>
      <p className="text-muted mb-4">
        Décrivez votre activité pour que MyTour s'adapte au mieux à vos besoins.
      </p>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Splitter>
          <FormGroup floating>
            <Controller
              name="jobCategory"
              control={control}
              render={({field}) => (
                <Input
                  {...field}
                  type="select"
                  id="field-jobCategory"
                  placeholder="Catégorie de métier"
                >
                  {JOB_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </Input>
              )}
            />
            <Label for="field-jobCategory">Catégorie de métier</Label>
          </FormGroup>

          {jobCategory && availableJobs.length > 0 ? (
            <FormGroup floating>
              <Controller
                name="jobTitle"
                control={control}
                render={({field}) => (
                  <Input {...field} type="select" id="field-jobTitle" placeholder="Métier">
                    <option value="">--</option>
                    {availableJobs.map((job) => (
                      <option key={job} value={job}>
                        {job}
                      </option>
                    ))}
                  </Input>
                )}
              />
              <Label for="field-jobTitle">Métier</Label>
            </FormGroup>
          ) : (
            <div />
          )}
        </Splitter>

        <Button color="primary" type="submit" disabled={disabled}>
          {isUpdatingBusinessDescriptor ? <Spinner size="sm" /> : null}
          {isUpdatingBusinessDescriptor ? " " : ""}Sauvegarder
        </Button>
      </Form>
    </div>
  );

  async function onSubmit(data: BusinessDescriptorFormData) {
    try {
      const jobCategory =
        data.jobCategory && JobCategoryArray.includes(data.jobCategory as JobCategory)
          ? (data.jobCategory as JobCategory)
          : null;
      await updateBusinessDescriptor({
        jobCategory,
        jobTitle: data.jobTitle,
      });
      toast.success("Descriptif enregistré");
    } catch {
      toast.error("Erreur lors de la sauvegarde du descriptif");
    }
  }
}
